"use client";

import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useController } from "react-hook-form";
import { Turnstile } from "@marsidev/react-turnstile";
import * as z from "zod";
import { TipSelection } from "./TipSelection";
import { ServiceType } from "./booking";
import { StripePaymentForm } from "./StripePaymentForm";
import { CustomerInfoPaymentView } from "./CustomerInfoPaymentView";

/**
 * CustomerInfoPaymentStep — production details/payment step.
 *
 * Owns form state, zod validation, Stripe, tips, Turnstile.
 * All visual geometry is rendered by CustomerInfoPaymentView.
 */

function formatPhoneInput(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const formSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z
    .string()
    .refine((v) => v.replace(/\D/g, "").length === 10, "A valid phone number is required"),
  specialRequests: z.string().max(500).optional(),
  contactMethod: z.enum(["email", "phone"]),
  agreements: z.object({
    reminders: z.boolean().default(true),
    marketing: z.boolean().optional(),
    termsAccepted: z.boolean().refine((accepted) => accepted, {
      message: "You must accept the Terms of Service and Privacy Policy"
    })
  }),
  paymentMethod: z.string().min(1, "Please select a payment method")
});
export type CustomerInfoPaymentData = z.infer<typeof formSchema>;
export interface CustomerInfoPaymentStepProps {
  customerInfo: CustomerInfoPaymentData | null;
  onUpdateCustomerInfo: (info: CustomerInfoPaymentData | null) => void;
  selectedService?: ServiceType;
  tipAmount?: number;
  onTipChange?: (amount: number) => void;
  businessId?: string;
  onPaymentSuccess?: (paymentData?: {
    stripeCustomerId?: string;
    paymentMethodId?: string;
    paymentIntentId?: string;
  }) => void;
  onPaymentError?: (error: string) => void;
  hasDateTimeSelected?: boolean;
  bookingId?: string | null;
  onVerify?: (token: string) => void;
  cardPaymentsEnabled?: boolean;
  cancellationPolicy?: string | null;
  onPaymentMethodChange?: (method: string) => void;
}

export default function CustomerInfoPaymentStep({
  customerInfo,
  onUpdateCustomerInfo,
  selectedService,
  tipAmount = 0,
  onTipChange,
  businessId,
  onPaymentSuccess,
  onPaymentError,
  hasDateTimeSelected = true,
  bookingId,
  onVerify,
  cardPaymentsEnabled = true,
  cancellationPolicy,
  onPaymentMethodChange,
}: CustomerInfoPaymentStepProps) {
  const form = useForm<CustomerInfoPaymentData>({
    resolver: zodResolver(formSchema),
    defaultValues: customerInfo || {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      specialRequests: "",
      contactMethod: "email",
      agreements: {
        reminders: true,
        marketing: false,
        termsAccepted: false
      },
      paymentMethod: cardPaymentsEnabled ? "new_card" : "in_person"
    }
  });

  const firstName = useController({ control: form.control, name: "firstName" });
  const lastName = useController({ control: form.control, name: "lastName" });
  const email = useController({ control: form.control, name: "email" });
  const phone = useController({ control: form.control, name: "phone" });
  const specialRequests = useController({ control: form.control, name: "specialRequests" });
  const paymentMethod = useController({ control: form.control, name: "paymentMethod" });
  const marketing = useController({ control: form.control, name: "agreements.marketing" });
  const termsAccepted = useController({ control: form.control, name: "agreements.termsAccepted" });

  const onUpdateCustomerInfoRef = React.useRef(onUpdateCustomerInfo);
  React.useEffect(() => {
    onUpdateCustomerInfoRef.current = onUpdateCustomerInfo;
  }, [onUpdateCustomerInfo]);

  React.useEffect(() => {
    const subscription = form.watch((value) => {
      const errors = form.formState.errors;
      // Terms acceptance is intentionally NOT part of this gate: the checkbox
      // renders below the payment section, and gating here would leave the
      // Stripe form stuck on its "complete your personal details above"
      // placeholder with every visible field filled in. Terms are enforced at
      // submission instead — zod blocks the in-person submit, and the Stripe
      // Pay button is disabled until the box is checked.
      const hasPaymentFieldErrors = !!(
        errors.firstName ||
        errors.lastName ||
        errors.email ||
        errors.phone ||
        errors.paymentMethod
      );
      const hasRequiredFields = !!(
        value.firstName &&
        value.lastName &&
        value.email &&
        value.phone &&
        value.paymentMethod
      );

      if (!hasPaymentFieldErrors && hasRequiredFields) {
        onUpdateCustomerInfoRef.current(value as CustomerInfoPaymentData);
      } else {
        onUpdateCustomerInfoRef.current(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const onSubmit = () => {
    window.dispatchEvent(new CustomEvent('customer-info-payment-success'));
  };

  const paymentMethodValue = paymentMethod.field.value;

  return (
    <CustomerInfoPaymentView
      asForm
      formId="customer-info-payment-form"
      onSubmit={form.handleSubmit(onSubmit)}
      firstName={{
        value: firstName.field.value || "",
        onChange: firstName.field.onChange,
        onBlur: firstName.field.onBlur,
        error: firstName.fieldState.error?.message,
      }}
      lastName={{
        value: lastName.field.value || "",
        onChange: lastName.field.onChange,
        onBlur: lastName.field.onBlur,
        error: lastName.fieldState.error?.message,
      }}
      email={{
        value: email.field.value || "",
        onChange: email.field.onChange,
        onBlur: email.field.onBlur,
        error: email.fieldState.error?.message,
      }}
      phone={{
        value: phone.field.value || "",
        onChange: (v) => phone.field.onChange(formatPhoneInput(v)),
        onBlur: phone.field.onBlur,
        error: phone.fieldState.error?.message,
      }}
      specialRequests={{
        value: specialRequests.field.value || "",
        onChange: specialRequests.field.onChange,
        onBlur: specialRequests.field.onBlur,
      }}
      paymentMethod={paymentMethodValue}
      onPaymentMethodChange={(method) => {
        paymentMethod.field.onChange(method);
        onPaymentMethodChange?.(method);
      }}
      cardPaymentsEnabled={cardPaymentsEnabled}
      selectedService={selectedService}
      tipAmount={tipAmount}
      tipSection={
        selectedService && onTipChange && paymentMethodValue && paymentMethodValue !== "in_person" ? (
          <div style={{ marginTop: 28 }}>
            <TipSelection
              baseAmount={selectedService.price}
              selectedTip={tipAmount}
              onTipChange={onTipChange}
            />
          </div>
        ) : null
      }
      paymentSection={
        // Match main: Stripe lived inside the tip/card extras gate (requires onTipChange).
        selectedService &&
        onTipChange &&
        paymentMethodValue &&
        paymentMethodValue !== "in_person" &&
        paymentMethodValue === "new_card" ? (
          <div style={{ marginTop: 24 }}>
            <StripePaymentForm
              businessId={businessId}
              selectedService={selectedService}
              tipAmount={tipAmount}
              customerInfo={customerInfo ? {
                firstName: customerInfo.firstName,
                lastName: customerInfo.lastName,
                email: customerInfo.email,
                phone: customerInfo.phone,
              } : undefined}
              onPaymentSuccess={onPaymentSuccess}
              onPaymentError={onPaymentError}
              hasDateTimeSelected={hasDateTimeSelected}
              bookingId={bookingId || undefined}
              termsAccepted={!!termsAccepted.field.value}
            />
          </div>
        ) : null
      }
      marketingChecked={!!marketing.field.value}
      onMarketingChange={(checked) => marketing.field.onChange(checked)}
      showMarketingCheckbox
      termsChecked={!!termsAccepted.field.value}
      onTermsChange={(checked) => termsAccepted.field.onChange(checked)}
      termsError={termsAccepted.fieldState.error?.message}
      cancellationPolicy={cancellationPolicy}
      turnstileSection={
        onVerify ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
            <Turnstile
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "1x00000000000000000000AA"}
              onSuccess={onVerify}
            />
          </div>
        ) : null
      }
    />
  );
}
